<script setup lang="ts">
import { computed } from 'vue';
import LabelText from '@/components/forms/LabelText.vue';
import ErrorMessage from '../ErrorMessage.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Object],
    default: ''
  },
  options: {
    type: Array as () => Array<{label: string, value: unknown}>,
    required: true
  },
  id: {
    type: String,
    required: true
  },
  labelText: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Select an option'
  },
  size: {
    type: String as () => 'xss' | 'xs' | 'sm' | 'md' | 'lg',
    default: 'md'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  editable: {
    type: Boolean,
    default: false
  },
  filter: {
    type: Boolean,
    default: false
  },
  asterisk: {
    type: Boolean,
    default: false
  },
  parentClass: {
    type: String,
    default: ''
  },
  // New error-related props
  errorMessage: {
    type: [String, Array] as unknown as () => string | string[],
    default: ''
  },
  errorType: {
    type: String as () => 'error' | 'warning' | 'info',
    default: 'error'
  },
});

const emit = defineEmits(['update:modelValue']);

const sizeClasses = {
  xss: 'xss',
  xs: 'xs',
  sm: 'sm',
  md: 'md',
  lg: 'lg'
};

// Error handling similar to TextInput
const hasError = computed(() => {
  return !!props.errorMessage &&
    (typeof props.errorMessage === 'string' ? props.errorMessage.length > 0 : props.errorMessage.length > 0)
});

// Dynamic class for error state
const dynamicSelectClasses = computed(() => {
  if (hasError.value && props.errorType === 'error') {
    return 'has-error'
  }
  return '';
});
</script>

<template>
  <div :class="['input-wrapper flex flex-col gap-2', parentClass]">
    <!-- Label -->
    <LabelText
      v-if="labelText"
      :labelText="labelText"
      :inputId="id"
      :asterisk="asterisk"
    />

    <!-- Select Component -->
    <SelectPrime
      :filter="filter"
      :editable="editable"
      :modelValue="modelValue"
      @update:modelValue="(value: any) => emit('update:modelValue', value)"
      :options="options"
      optionLabel="label"
      optionValue="value"
      :disabled="disabled"
      :placeholder="placeholder"
      :class="[
        'w-full',
        sizeClasses[size],
        'global-select form-input',
        dynamicSelectClasses
      ]"
      :listStyle="{ maxHeight: '200px' }"
      :aria-labelledby="labelText ? id : undefined"
      :aria-label="labelText ? undefined : (placeholder || 'Select an option')"
      :aria-invalid="hasError"
    />

    <!-- Error message below select -->
    <ErrorMessage
      :message="errorMessage"
      :type="errorType"
    />
  </div>
</template>

<style scoped>
.p-select-overlay,
.p-select-overlay * {
  pointer-events: auto;
}
</style>
